/**
 * Pathology synoptic templates — organ-specific report skeletons.
 *
 * Each template is a light JSON schema describing the sections and
 * fields a pathologist fills out. Templates are loosely modelled on
 * the CAP cancer-protocol electronic cancer checklists (eCC) — the
 * ACP/RCPA/ICCR alignment tracks the same structured-reporting
 * movement. The schemas below are reporter-friendly subsets: the
 * fields a reader actually types into during sign-off, not the full
 * CAP JSON DTO. Extending to full CAP compliance is a follow-up —
 * the wire shape already stores free-form jsonb so no migration.
 *
 * Field types:
 *   text      — single line free text
 *   textarea  — multi-line free text
 *   number    — numeric input (with optional step / min / max)
 *   select    — dropdown from `options`
 *   date      — ISO date picker
 *
 * Field ids are what land in `path_reports.body` jsonb — shared
 * across templates where possible (specimen, diagnosis, comment)
 * so switching templates doesn't lose common narrative. Template-
 * specific fields use a domain-prefixed id (gleason_primary,
 * breslow_mm, etc.).
 */

export type FieldType = "text" | "textarea" | "number" | "select" | "date" | "icd10";

export interface TemplateField {
  id:          string;
  label:       string;
  type:        FieldType;
  /** `select` only — dropdown options. "—" for unspecified. */
  options?:    string[];
  default?:    string;
  placeholder?: string;
  /** Small hint rendered next to the label. */
  hint?:       string;
  /** Numeric only — step/min/max. */
  step?:       number | string;
  min?:        number;
  max?:        number;
  /** Rows for textarea (default 3). */
  rows?:       number;
  /** AI data-source id; the panel auto-fills from this when the
   *  corresponding aiResult signal populates. */
  aiSource?:   "ki67";
}

export interface TemplateSection {
  title:   string;
  fields:  TemplateField[];
  /** Grid layout for the fields — `cols-1` stacks them, `cols-2`
   *  pairs them side-by-side, etc. */
  layout?: "cols-1" | "cols-2" | "cols-3" | "cols-4";
}

export interface SynopticTemplate {
  id:          string;
  name:        string;
  /** Broad organ family — used by the picker to group templates. */
  specialty:   "breast" | "colorectal" | "lung" | "prostate" | "skin"
             | "gyn"    | "lymphoma"   | "renal"| "thyroid"  | "gi"
             | "generic";
  description: string;
  sections:    TemplateSection[];
}

/* ============================================================
 * Shared helpers — narrative fields that appear in every template
 * ============================================================ */
const narrativeDiagnosis: TemplateField = {
  id: "diagnosis", label: "Diagnosis (free text)", type: "textarea", rows: 3,
  placeholder: "Final impression, grade, stage if applicable",
};
const narrativeClinical: TemplateField = {
  id: "clinicalHistory", label: "Clinical history", type: "textarea", rows: 2,
};
const narrativeComment: TemplateField = {
  id: "comment", label: "Comment", type: "textarea", rows: 3,
  placeholder: "Additional findings, recommended IHC / molecular testing, …",
};
const specimenId: TemplateField = {
  id: "specimen", label: "Specimen", type: "text",
  placeholder: "Anatomic site + procedure",
};
/** Shared across every template — ICD-10-CM diagnosis codes for
 *  billing / EHR problem-list integration. Rendered as a
 *  type-ahead combobox backed by /viewer-api/icd10/search. The
 *  stored value is an array of { code, code_dotted, long_desc }. */
const icd10Codes: TemplateField = {
  id:    "icd10Codes",
  label: "ICD-10-CM diagnosis codes",
  hint:  "type to search · e.g. “breast ca”, “C50.911”, “melanoma”",
  type:  "icd10",
};

/* ============================================================
 * Templates
 * ============================================================ */

const BREAST_CORE: SynopticTemplate = {
  id: "breast-core",
  name: "Breast — core biopsy",
  specialty: "breast",
  description: "CAP breast core-needle protocol: IDC / DCIS / ILC scoring + IHC panel.",
  sections: [
    {
      title: "Specimen",
      layout: "cols-1",
      fields: [
        { ...specimenId, default: "Left breast, core biopsy" },
        narrativeClinical,
      ],
    },
    {
      title: "Tumour",
      layout: "cols-2",
      fields: [
        { id: "laterality", label: "Laterality", type: "select",
          options: ["left", "right", "unspecified"], default: "left" },
        { id: "tumorSizeMm", label: "Tumour size (mm)", type: "number",
          step: 0.1, min: 0 },
        { id: "histologyType", label: "Histologic type", type: "text",
          default: "Invasive ductal carcinoma" },
        { id: "grade", label: "Nottingham grade", type: "select",
          options: ["—", "1", "2", "3"], default: "—" },
      ],
    },
    {
      title: "Immunohistochemistry",
      layout: "cols-4",
      fields: [
        { id: "er",   label: "ER",   type: "select", options: ["—", "positive", "negative"] },
        { id: "pr",   label: "PR",   type: "select", options: ["—", "positive", "negative"] },
        { id: "her2", label: "HER2", type: "select", options: ["—", "0", "1+", "2+", "3+"] },
        { id: "ki67Pct", label: "Ki-67 (%)", type: "number",
          step: 0.1, min: 0, max: 100, aiSource: "ki67" },
      ],
    },
    {
      title: "Narrative",
      fields: [narrativeDiagnosis, narrativeComment, icd10Codes],
    },
  ],
};

const BREAST_SURGICAL: SynopticTemplate = {
  id: "breast-surgical",
  name: "Breast — lumpectomy / mastectomy",
  specialty: "breast",
  description: "Breast surgical excision: margins, lymph nodes, TNM stage.",
  sections: [
    {
      title: "Specimen",
      layout: "cols-1",
      fields: [
        { ...specimenId, default: "Right breast, lumpectomy" },
        narrativeClinical,
      ],
    },
    {
      title: "Tumour",
      layout: "cols-2",
      fields: [
        { id: "laterality", label: "Laterality", type: "select",
          options: ["left", "right", "unspecified"], default: "left" },
        { id: "tumorSizeMm", label: "Tumour size (mm)", type: "number",
          step: 0.1, min: 0 },
        { id: "histologyType", label: "Histologic type", type: "text",
          default: "Invasive ductal carcinoma" },
        { id: "grade", label: "Nottingham grade", type: "select",
          options: ["—", "1", "2", "3"], default: "—" },
      ],
    },
    {
      title: "Margins",
      layout: "cols-2",
      fields: [
        { id: "closestMarginMm", label: "Closest margin (mm)", type: "number",
          step: 0.1, min: 0 },
        { id: "marginStatus", label: "Margin status", type: "select",
          options: ["—", "negative", "close (<2 mm)", "positive"], default: "—" },
        { id: "marginDirection", label: "Closest margin direction", type: "text",
          placeholder: "e.g. deep, anterior, medial" },
      ],
    },
    {
      title: "Lymph nodes",
      layout: "cols-2",
      fields: [
        { id: "lnExamined", label: "Nodes examined (n)", type: "number", step: 1, min: 0 },
        { id: "lnPositive", label: "Nodes positive (n)", type: "number", step: 1, min: 0 },
        { id: "lnExtracapsular", label: "Extracapsular extension", type: "select",
          options: ["—", "absent", "present"], default: "—" },
      ],
    },
    {
      title: "Immunohistochemistry + stage",
      layout: "cols-4",
      fields: [
        { id: "er",   label: "ER",   type: "select", options: ["—", "positive", "negative"] },
        { id: "pr",   label: "PR",   type: "select", options: ["—", "positive", "negative"] },
        { id: "her2", label: "HER2", type: "select", options: ["—", "0", "1+", "2+", "3+"] },
        { id: "ki67Pct", label: "Ki-67 (%)", type: "number", step: 0.1, min: 0, max: 100, aiSource: "ki67" },
        { id: "pT", label: "pT", type: "select",
          options: ["—", "Tis", "T1mi", "T1a", "T1b", "T1c", "T2", "T3", "T4"] },
        { id: "pN", label: "pN", type: "select",
          options: ["—", "N0", "N1mi", "N1a", "N1b", "N1c", "N2a", "N2b", "N3a", "N3b", "N3c"] },
        { id: "pM", label: "pM", type: "select", options: ["—", "MX", "M0", "M1"] },
      ],
    },
    {
      title: "Narrative",
      fields: [narrativeDiagnosis, narrativeComment, icd10Codes],
    },
  ],
};

const COLON_RESECTION: SynopticTemplate = {
  id: "colon-resection",
  name: "Colon — resection",
  specialty: "colorectal",
  description: "Colorectal carcinoma: depth of invasion, LN, margins, MMR / MSI.",
  sections: [
    {
      title: "Specimen",
      layout: "cols-1",
      fields: [
        { ...specimenId, default: "Right hemicolectomy" },
        narrativeClinical,
      ],
    },
    {
      title: "Tumour",
      layout: "cols-2",
      fields: [
        { id: "tumorSite", label: "Anatomic site", type: "select",
          options: ["—", "caecum", "ascending colon", "hepatic flexure", "transverse colon",
                   "splenic flexure", "descending colon", "sigmoid colon", "rectum"] },
        { id: "histologyType", label: "Histologic type", type: "select",
          options: ["Adenocarcinoma NOS", "Mucinous adenocarcinoma",
                   "Signet-ring cell carcinoma", "Medullary carcinoma", "Other"],
          default: "Adenocarcinoma NOS" },
        { id: "grade", label: "Grade", type: "select",
          options: ["—", "low grade (well / moderately diff.)", "high grade (poorly diff. / undiff.)"],
          default: "—" },
        { id: "invasionDepth", label: "Depth of invasion (pT)", type: "select",
          options: ["—", "Tis", "T1", "T2", "T3", "T4a", "T4b"] },
      ],
    },
    {
      title: "Margins",
      layout: "cols-2",
      fields: [
        { id: "proximalMargin", label: "Proximal margin", type: "select",
          options: ["—", "uninvolved", "involved"] },
        { id: "distalMargin",   label: "Distal margin",   type: "select",
          options: ["—", "uninvolved", "involved"] },
        { id: "radialMargin",   label: "Radial / CRM",    type: "select",
          options: ["—", "uninvolved (>1 mm)", "involved (≤1 mm)"] },
      ],
    },
    {
      title: "Lymph nodes + LVI",
      layout: "cols-2",
      fields: [
        { id: "lnExamined", label: "Nodes examined (n)", type: "number", step: 1, min: 0 },
        { id: "lnPositive", label: "Nodes positive (n)", type: "number", step: 1, min: 0 },
        { id: "lvi", label: "Lymphovascular invasion", type: "select",
          options: ["—", "absent", "present"] },
        { id: "pni", label: "Perineural invasion", type: "select",
          options: ["—", "absent", "present"] },
      ],
    },
    {
      title: "Molecular",
      layout: "cols-3",
      fields: [
        { id: "mmrStatus", label: "MMR IHC", type: "select",
          options: ["—", "intact (pMMR)", "loss (dMMR)"] },
        { id: "msiStatus", label: "MSI", type: "select",
          options: ["—", "MSS", "MSI-L", "MSI-H"] },
        { id: "kras", label: "KRAS", type: "select",
          options: ["—", "wild-type", "mutant", "pending"] },
      ],
    },
    {
      title: "Narrative",
      fields: [narrativeDiagnosis, narrativeComment, icd10Codes],
    },
  ],
};

const LUNG_RESECTION: SynopticTemplate = {
  id: "lung-resection",
  name: "Lung — biopsy / resection",
  specialty: "lung",
  description: "NSCLC / SCLC with targeted-therapy biomarker panel.",
  sections: [
    {
      title: "Specimen",
      layout: "cols-1",
      fields: [
        { ...specimenId, default: "Right upper lobectomy" },
        narrativeClinical,
      ],
    },
    {
      title: "Tumour",
      layout: "cols-2",
      fields: [
        { id: "laterality", label: "Laterality", type: "select",
          options: ["left", "right", "unspecified"], default: "right" },
        { id: "tumorSite", label: "Lobe", type: "select",
          options: ["—", "RUL", "RML", "RLL", "LUL", "lingula", "LLL", "main bronchus"] },
        { id: "tumorSizeMm", label: "Tumour size (mm)", type: "number", step: 0.1, min: 0 },
        { id: "histologyType", label: "Histologic type", type: "select",
          options: ["Adenocarcinoma", "Squamous cell carcinoma", "Large cell carcinoma",
                   "Small cell carcinoma", "Adenosquamous carcinoma", "Carcinoid", "Other"],
          default: "Adenocarcinoma" },
        { id: "grade", label: "Grade", type: "select",
          options: ["—", "G1 well diff.", "G2 mod. diff.", "G3 poorly diff.", "G4 undiff."] },
      ],
    },
    {
      title: "Invasion + margins",
      layout: "cols-2",
      fields: [
        { id: "visceralPleura", label: "Visceral pleural invasion", type: "select",
          options: ["—", "absent", "present"] },
        { id: "bronchialMargin", label: "Bronchial margin", type: "select",
          options: ["—", "uninvolved", "involved"] },
        { id: "vascularInvasion", label: "Vascular invasion", type: "select",
          options: ["—", "absent", "present"] },
        { id: "parenchymalMargin", label: "Parenchymal margin", type: "select",
          options: ["—", "uninvolved", "involved"] },
      ],
    },
    {
      title: "Biomarkers",
      layout: "cols-3",
      fields: [
        { id: "egfr",   label: "EGFR",   type: "select",
          options: ["—", "wild-type", "mutant (specify)", "pending"] },
        { id: "alk",    label: "ALK",    type: "select",
          options: ["—", "negative", "rearranged", "pending"] },
        { id: "ros1",   label: "ROS1",   type: "select",
          options: ["—", "negative", "rearranged", "pending"] },
        { id: "pdl1TPS",label: "PD-L1 TPS (%)", type: "number",
          step: 1, min: 0, max: 100 },
        { id: "kras",   label: "KRAS",   type: "select",
          options: ["—", "wild-type", "G12C", "other mutant", "pending"] },
        { id: "braf",   label: "BRAF",   type: "select",
          options: ["—", "wild-type", "V600E", "other mutant", "pending"] },
      ],
    },
    {
      title: "Stage",
      layout: "cols-3",
      fields: [
        { id: "pT", label: "pT", type: "select",
          options: ["—", "Tis", "T1a", "T1b", "T1c", "T2a", "T2b", "T3", "T4"] },
        { id: "pN", label: "pN", type: "select",
          options: ["—", "N0", "N1", "N2", "N3"] },
        { id: "pM", label: "pM", type: "select", options: ["—", "M0", "M1a", "M1b", "M1c"] },
      ],
    },
    {
      title: "Narrative",
      fields: [narrativeDiagnosis, narrativeComment, icd10Codes],
    },
  ],
};

const PROSTATE_BIOPSY: SynopticTemplate = {
  id: "prostate-biopsy",
  name: "Prostate — needle biopsy",
  specialty: "prostate",
  description: "Gleason grading + ISUP grade group per core.",
  sections: [
    {
      title: "Specimen",
      layout: "cols-1",
      fields: [
        { ...specimenId, default: "Prostate needle core biopsies, 12 cores" },
        narrativeClinical,
      ],
    },
    {
      title: "Tumour",
      layout: "cols-2",
      fields: [
        { id: "coresPositive", label: "Cores positive (n)", type: "number", step: 1, min: 0 },
        { id: "coresTotal", label: "Cores total (n)", type: "number", step: 1, min: 0 },
        { id: "maxCoreInvolvementPct", label: "Max core involvement (%)", type: "number",
          step: 1, min: 0, max: 100 },
        { id: "bilateral", label: "Bilateral involvement", type: "select",
          options: ["—", "absent", "present"] },
      ],
    },
    {
      title: "Grading",
      layout: "cols-3",
      fields: [
        { id: "gleasonPrimary", label: "Gleason primary", type: "select",
          options: ["—", "3", "4", "5"], default: "—" },
        { id: "gleasonSecondary", label: "Gleason secondary", type: "select",
          options: ["—", "3", "4", "5"], default: "—" },
        { id: "gleasonTertiary", label: "Tertiary ≥4 present", type: "select",
          options: ["—", "absent", "present"] },
        { id: "isupGroup", label: "ISUP grade group", type: "select",
          options: ["—", "1", "2", "3", "4", "5"] },
      ],
    },
    {
      title: "Other findings",
      layout: "cols-2",
      fields: [
        { id: "pni", label: "Perineural invasion", type: "select", options: ["—", "absent", "present"] },
        { id: "lvi", label: "Lymphovascular invasion", type: "select", options: ["—", "absent", "present"] },
        { id: "extraprostaticExt", label: "Extraprostatic extension (when visible)", type: "select",
          options: ["—", "absent", "present", "not assessable"] },
      ],
    },
    {
      title: "Narrative",
      fields: [narrativeDiagnosis, narrativeComment, icd10Codes],
    },
  ],
};

const SKIN_EXCISION: SynopticTemplate = {
  id: "skin-excision",
  name: "Skin — biopsy / excision",
  specialty: "skin",
  description: "BCC / SCC / melanoma: Breslow depth, Clark level, margins, ulceration.",
  sections: [
    {
      title: "Specimen",
      layout: "cols-1",
      fields: [
        { ...specimenId, default: "Skin, shave biopsy" },
        narrativeClinical,
        { id: "site", label: "Anatomic site", type: "text",
          placeholder: "e.g. left forearm, nasal bridge" },
      ],
    },
    {
      title: "Tumour",
      layout: "cols-2",
      fields: [
        { id: "histologyType", label: "Histologic type", type: "select",
          options: ["Basal cell carcinoma", "Squamous cell carcinoma", "Melanoma",
                   "Actinic keratosis", "Dysplastic nevus", "Benign nevus", "Other"],
          default: "Basal cell carcinoma" },
        { id: "subtype", label: "Subtype / pattern", type: "text",
          placeholder: "e.g. nodular, superficial, infiltrative" },
        { id: "breslowMm", label: "Breslow thickness (mm)", type: "number", step: 0.01, min: 0,
          hint: "melanoma" },
        { id: "clarkLevel", label: "Clark level", type: "select",
          options: ["—", "I", "II", "III", "IV", "V"], hint: "melanoma" },
      ],
    },
    {
      title: "Findings",
      layout: "cols-2",
      fields: [
        { id: "ulceration", label: "Ulceration", type: "select", options: ["—", "absent", "present"] },
        { id: "mitoses", label: "Mitoses (/mm²)", type: "number", step: 1, min: 0, hint: "melanoma" },
        { id: "regression", label: "Regression", type: "select", options: ["—", "absent", "partial", "complete"] },
        { id: "satelliteMets", label: "Satellite / in-transit mets", type: "select", options: ["—", "absent", "present"] },
      ],
    },
    {
      title: "Margins",
      layout: "cols-2",
      fields: [
        { id: "peripheralMargin", label: "Peripheral margin", type: "select",
          options: ["—", "uninvolved", "involved"] },
        { id: "deepMargin", label: "Deep margin", type: "select",
          options: ["—", "uninvolved", "involved"] },
        { id: "closestMarginMm", label: "Closest margin (mm)", type: "number", step: 0.1, min: 0 },
      ],
    },
    {
      title: "Narrative",
      fields: [narrativeDiagnosis, narrativeComment, icd10Codes],
    },
  ],
};

const ENDOMETRIAL: SynopticTemplate = {
  id: "endometrial",
  name: "Endometrial — biopsy / hysterectomy",
  specialty: "gyn",
  description: "Endometrial carcinoma: grade, myometrial invasion, LVSI, MMR.",
  sections: [
    {
      title: "Specimen",
      layout: "cols-1",
      fields: [
        { ...specimenId, default: "Endometrial biopsy / curettage" },
        narrativeClinical,
      ],
    },
    {
      title: "Tumour",
      layout: "cols-2",
      fields: [
        { id: "histologyType", label: "Histologic type", type: "select",
          options: ["Endometrioid carcinoma", "Serous carcinoma", "Clear cell carcinoma",
                   "Mucinous carcinoma", "Undifferentiated / dedifferentiated", "Carcinosarcoma"],
          default: "Endometrioid carcinoma" },
        { id: "figoGrade", label: "FIGO grade", type: "select",
          options: ["—", "1", "2", "3"] },
        { id: "myometrialInvasionPct", label: "Myometrial invasion (%)", type: "number",
          step: 1, min: 0, max: 100 },
        { id: "lvsi", label: "LVSI", type: "select", options: ["—", "absent", "focal", "substantial"] },
      ],
    },
    {
      title: "Extent",
      layout: "cols-2",
      fields: [
        { id: "cervicalInvolvement", label: "Cervical stroma invasion", type: "select",
          options: ["—", "absent", "present"] },
        { id: "adnexalInvolvement", label: "Adnexal involvement", type: "select",
          options: ["—", "absent", "present"] },
        { id: "pT", label: "pT (FIGO 2009)", type: "select",
          options: ["—", "T1a", "T1b", "T2", "T3a", "T3b", "T4"] },
        { id: "pN", label: "pN", type: "select", options: ["—", "N0", "N1", "N2"] },
      ],
    },
    {
      title: "Molecular",
      layout: "cols-3",
      fields: [
        { id: "mmrStatus", label: "MMR IHC", type: "select",
          options: ["—", "intact (pMMR)", "loss (dMMR)"] },
        { id: "p53", label: "p53", type: "select",
          options: ["—", "wild-type", "aberrant (mutant pattern)"] },
        { id: "molSubtype", label: "TCGA group", type: "select",
          options: ["—", "POLE-mutated", "MSI-high", "Copy-number low", "Copy-number high (p53 abnormal)"] },
      ],
    },
    {
      title: "Narrative",
      fields: [narrativeDiagnosis, narrativeComment, icd10Codes],
    },
  ],
};

const GENERIC: SynopticTemplate = {
  id: "generic",
  name: "Generic surgical specimen",
  specialty: "generic",
  description: "Free-form template for specimens without a dedicated CAP protocol.",
  sections: [
    {
      title: "Specimen",
      layout: "cols-1",
      fields: [
        specimenId,
        narrativeClinical,
        { id: "grossDescription", label: "Gross description", type: "textarea", rows: 4 },
      ],
    },
    {
      title: "Microscopic",
      layout: "cols-1",
      fields: [
        { id: "microscopicDescription", label: "Microscopic description", type: "textarea", rows: 5 },
      ],
    },
    {
      title: "Narrative",
      fields: [narrativeDiagnosis, narrativeComment, icd10Codes],
    },
  ],
};

/* ============================================================
 * Registry + resolver
 * ============================================================ */

export const SYNOPTIC_TEMPLATES: SynopticTemplate[] = [
  BREAST_CORE,
  BREAST_SURGICAL,
  COLON_RESECTION,
  LUNG_RESECTION,
  PROSTATE_BIOPSY,
  SKIN_EXCISION,
  ENDOMETRIAL,
  GENERIC,
];

export function templateById(id: string | undefined | null): SynopticTemplate {
  if (!id) return BREAST_CORE;
  return SYNOPTIC_TEMPLATES.find((t) => t.id === id) ?? BREAST_CORE;
}

/**
 * Heuristic template picker — scan the slide description +
 * patient name for organ keywords and return the best match.
 * Default is breast-core (the highest-volume specimen in most
 * histopath labs + what the previous hardcoded template used),
 * so reports that don't match any keyword stay compatible with
 * what readers are used to.
 */
export function guessTemplate(
  ...signals: Array<string | undefined | null>
): SynopticTemplate {
  const blob = signals.filter(Boolean).join(" · ").toLowerCase();
  if (!blob) return BREAST_CORE;
  // Ordered most-specific → least-specific. Skin / melanoma first
  // because "skin" also appears inside "foreskin" type descriptions,
  // and we want the melanoma / BCC route ahead of the generic fall.
  if (/melanoma|\bbcc\b|\bscc\b|\bskin\b|dermatopath/.test(blob))       return SKIN_EXCISION;
  if (/prostate|\btrus\b|\brp\b|gleason/.test(blob))                    return PROSTATE_BIOPSY;
  if (/lung|pulmonary|lobectomy|bronch|nsclc|sclc/.test(blob))          return LUNG_RESECTION;
  if (/colon|rectum|rectal|colorectal|hemicolectomy|polyp/.test(blob))  return COLON_RESECTION;
  if (/endometri|uterus|hysterect|gyn|ovarian|cervi/.test(blob))        return ENDOMETRIAL;
  if (/breast|mammary|lumpect|mastect|ihc|nottingham|ki67|her2/.test(blob)) {
    return /mastect|lumpect|excision|surg/.test(blob) ? BREAST_SURGICAL : BREAST_CORE;
  }
  return GENERIC;
}
